import React from "react";
import {
  Avatar,
  Box,
  CardContent,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import FlashOnRoundedIcon from "@mui/icons-material/FlashOnRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import WhatshotRoundedIcon from "@mui/icons-material/WhatshotRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { ServiceOptionCard, ProviderCard } from "../../styles/StyledComponents";
import { fornitori } from "../../constants";
import BillUploadSection from "../BillUploadSection";

function Step0Service({
  formData,
  errors,
  handleChange,
  methodChosen,
  uploadedFile,
  isAnalyzing,
  analysisSuccess,
  analysisError,
  handleDragOver,
  handleFileDrop,
  handleFileSelect,
  handleManualEntry,
  handleRemoveFile,
}) {
  return (
    <Box>
      {/* Upload Bolletta Section - mostra solo se non è stata scelta modalità manuale */}
      {methodChosen !== "manual" && (
        <BillUploadSection
          uploadedFile={uploadedFile}
          isAnalyzing={isAnalyzing}
          analysisSuccess={analysisSuccess}
          analysisError={analysisError}
          handleDragOver={handleDragOver}
          handleFileDrop={handleFileDrop}
          handleFileSelect={handleFileSelect}
          handleManualEntry={handleManualEntry}
          handleRemoveFile={handleRemoveFile}
        />
      )}

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Di quale servizio hai bisogno?
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <ServiceOptionCard
            selected={formData.servizio === "luce"}
            onClick={() => handleChange("servizio", "luce")}
          >
            <CardContent sx={{ textAlign: "center", py: 3 }}>
              <FlashOnRoundedIcon
                sx={{ fontSize: 48, color: "warning.main", mb: 1 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Luce
              </Typography>
            </CardContent>
          </ServiceOptionCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <ServiceOptionCard
            selected={formData.servizio === "gas"}
            onClick={() => handleChange("servizio", "gas")}
          >
            <CardContent sx={{ textAlign: "center", py: 3 }}>
              <LocalFireDepartmentRoundedIcon
                sx={{ fontSize: 48, color: "error.main", mb: 1 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Gas
              </Typography>
            </CardContent>
          </ServiceOptionCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <ServiceOptionCard
            selected={formData.servizio === "luce-gas"}
            onClick={() => handleChange("servizio", "luce-gas")}
          >
            <CardContent sx={{ textAlign: "center", py: 3 }}>
              <WhatshotRoundedIcon
                sx={{ fontSize: 48, color: "info.main", mb: 1 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Luce e Gas
              </Typography>
            </CardContent>
          </ServiceOptionCard>
        </Grid>
      </Grid>

      <TextField
        fullWidth
        label="Il tuo comune"
        placeholder="Inserisci il tuo comune..."
        value={formData.comune}
        onChange={(e) => handleChange("comune", e.target.value)}
        error={errors.comune}
        helperText={errors.comune ? "Campo obbligatorio" : ""}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocationOnRoundedIcon />
            </InputAdornment>
          ),
        }}
      />

      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ fontWeight: 600, mb: 2 }}
      >
        Tipologia di utenza
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <ServiceOptionCard
            selected={formData.tipologia === "privato"}
            onClick={() => handleChange("tipologia", "privato")}
          >
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <HomeRoundedIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Privato
              </Typography>
            </CardContent>
          </ServiceOptionCard>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ServiceOptionCard
            selected={formData.tipologia === "business"}
            onClick={() => handleChange("tipologia", "business")}
          >
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <BusinessRoundedIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Business
              </Typography>
            </CardContent>
          </ServiceOptionCard>
        </Grid>
      </Grid>

      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ fontWeight: 600, mb: 2 }}
      >
        Mercato di provenienza
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <ServiceOptionCard
            selected={formData.mercato === "tutelato"}
            onClick={() => handleChange("mercato", "tutelato")}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Mercato Tutelato
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Prezzi regolamentati
              </Typography>
            </CardContent>
          </ServiceOptionCard>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ServiceOptionCard
            selected={formData.mercato === "libero"}
            onClick={() => handleChange("mercato", "libero")}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Mercato Libero
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Prezzi di mercato
              </Typography>
            </CardContent>
          </ServiceOptionCard>
        </Grid>
      </Grid>

      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ fontWeight: 600, mb: 2 }}
      >
        Fornitore attuale
      </Typography>
      <Grid container spacing={2}>
        {fornitori.map((fornitore) => (
          <Grid item xs={6} sm={4} md={3} key={fornitore.id}>
            <ProviderCard
              selected={formData.fornitore === fornitore.id}
              onClick={() => handleChange("fornitore", fornitore.id)}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  py: 3,
                  position: "relative",
                }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    margin: "0 auto 12px",
                    bgcolor: fornitore.color,
                    fontSize: 24,
                    fontWeight: 700,
                  }}
                >
                  {fornitore.logo}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fornitore.name}
                </Typography>
              </CardContent>
            </ProviderCard>
          </Grid>
        ))}
      </Grid>
      {errors.fornitore && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 2, display: "block" }}
        >
          Seleziona un fornitore
        </Typography>
      )}
    </Box>
  );
}

export default Step0Service;
